import * as THREE from 'three';
import { GEAR_BASES, type GearBaseId } from '@somnhold/content';
import { DWOOD, cloth, metal, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { gearHeld, type GearRarity } from './gear.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The armory's rack by the forge (docs/EQUIPO.md §6, etapa 6): the group's best finds on show. A dark timber
 * rack on a stone footing, a red banner that reads from the table camera, and up to six pieces in a row: the
 * same models as in a hero's hand, with their rarity's runes. Baked like a structure (a few draws, its glows in
 * the shared glow layer); rebuilt only when a better piece comes in.
 */

export interface ArmeroLook {
  pieces: { base: GearBaseId; rarity: GearRarity }[];
}

/** The rack's key: its pieces in order (`armero:base.rarity,base.rarity…`). */
export const armeroKey = (look: ArmeroLook): string => `armero:${look.pieces.map((p) => `${p.base}.${p.rarity}`).join(',')}`;

/** A key read back (unknown bases dropped). */
export const parseArmero = (s: string | undefined): ArmeroLook => ({
  pieces: (s ?? '')
    .split(',')
    .map((x) => x.split('.'))
    .filter(([b]) => !!b && b in GEAR_BASES)
    .map(([b, r]) => ({ base: b as GearBaseId, rarity: (r ?? 'common') as GearRarity })),
});

const RED = cloth('#7a2328');
const IRON = metal('#4a4f57', { roughness: 0.5 });

export const armero = (o: BaseOpts & ArmeroLook): Model => {
  const c = begin(o, 0.9, 77);
  const { g } = c;
  const W = 2.3;
  // Footing: a low stone plinth.
  const foot = M(new THREE.BoxGeometry(W + 0.2, 0.14, 0.7), stone('#5d5a56'), g);
  foot.position.y = 0.07;
  // The rack: two posts, a top beam, a lower rail, iron pegs.
  for (const k of [-1, 1]) limb(g, V(k * (W / 2), 0.14, -0.18), V(k * (W / 2), 1.75, -0.18), 0.055, 0.06, DWOOD, 6);
  const beam = M(new THREE.BoxGeometry(W + 0.3, 0.12, 0.14), DWOOD, g);
  beam.position.set(0, 1.72, -0.18);
  const rail = M(new THREE.BoxGeometry(W, 0.07, 0.1), DWOOD, g);
  rail.position.set(0, 0.62, -0.18);
  for (const k of [-1, 1]) M(new THREE.ConeGeometry(0.07, 0.16, 4), IRON, g).position.set(k * (W / 2 + 0.15), 1.84, -0.18);
  // The banner, hanging from the beam's middle: red with a dark border, it marks the rack from far off.
  const banner = G(g, 0, 1.66, -0.24);
  M(new THREE.BoxGeometry(0.46, 0.62, 0.02), RED, banner).position.y = -0.31;
  M(new THREE.BoxGeometry(0.5, 0.05, 0.03), IRON, banner).position.y = 0.02;
  const tail = M(new THREE.ConeGeometry(0.23, 0.18, 3), RED, banner);
  tail.position.y = -0.7;
  tail.rotation.z = Math.PI;
  tail.scale.z = 0.08;
  // The pieces, in a row: weapons stand against the rail, the rest sit on short stands; each fits a slot.
  const n = o.pieces.length;
  const step = n > 1 ? (W - 0.5) / (n - 1) : 0;
  o.pieces.forEach((p, i) => {
    const x = n > 1 ? -(W - 0.5) / 2 + i * step : 0;
    const slot = GEAR_BASES[p.base].slot;
    const held = gearHeld(p.base, p.rarity);
    const box = new THREE.Box3().setFromObject(held);
    const h = box.max.y - box.min.y;
    const room = slot === 'weapon' ? 1.55 : 0.78;
    const k = Math.min(1.5, room / Math.max(0.01, h));
    const at = G(g, x, 0, 0.02);
    if (slot !== 'weapon') {
      // A short stand: a wooden post and a little shelf for the piece.
      limb(at, V(0, 0.14, 0), V(0, 0.68, 0), 0.03, 0.035, DWOOD, 5);
      M(new THREE.BoxGeometry(0.26, 0.04, 0.22), DWOOD, at).position.y = 0.7;
    }
    held.scale.multiplyScalar(k);
    held.position.y = (slot === 'weapon' ? 0.16 : 0.74) - box.min.y * k;
    held.rotation.z = slot === 'weapon' ? 0.08 : 0;
    at.add(held);
    // The rarest pieces glow a little at their foot, so the row reads at night too.
    if (p.rarity === 'veryRare' || p.rarity === 'unique') glowSprite(at, p.rarity === 'unique' ? '#ff4f3a' : '#b05cff', 0.55, V(0, slot === 'weapon' ? 0.5 : 0.95, 0.12), 0.35);
  });
  return done(c, 1.9);
};
